import { useParams } from "react-router-dom";
import { projectImages } from "../utils/helpers";
import projectList from "../Data/Projects";
import { useLayoutEffect, useRef, useEffect } from "react";

function getProjectImages(projectId, bannerImgName) {
  return {
    gallery:
      projectImages[projectId]
        ?.filter((image) => !image.src.includes(bannerImgName))
        .map((image) => image.src) ?? [],
    banner: projectImages[projectId]?.find((image) => {
      return image.src.includes(bannerImgName);
    })?.src,
  };
}

export default function Project() {
  const { id } = useParams();
  const projectData = projectList[id];
  const imageList = getProjectImages(id, projectData?.bannerImageName);

  // 1. Setup Refs for persistent memory and clean DOM targeting
  const titleContainerRef = useRef(null);
  const mediaRef = useRef(null);
  const titleTopRef = useRef(0);
  const titleContainerHeight = useRef("auto");

  // 2. Extracted measurement logic into a reusable function
  const measureLayout = () => {
    const navbar = document.querySelector(".navbar");
    if (!navbar || !titleContainerRef.current || !mediaRef.current) return;

    const navbar_height = navbar.offsetHeight;
    document.documentElement.style.setProperty(
      "--navbar-height",
      `${navbar_height}px`,
    );

    // Read exact geometry safely
    const header_rect = titleContainerRef.current.getBoundingClientRect();
    const media_height = mediaRef.current.getBoundingClientRect().height;

    // Save to mutable ref (persists across renders)
    titleTopRef.current = media_height - header_rect.height;
    // titleTopRef.current = header_rect.top + window.scrollY;
    titleContainerHeight.current = `${header_rect.height}px`; // 3. Set explicit height to prevent layout shift when fixed
    // console.log("Accurate titleTop established:", titleTopRef.current);
    // console.log("title height:", titleContainerHeight.current);
  };

  useLayoutEffect(() => {
    // Measure on mount for the rest of the text layout
    measureLayout();

    const handleScroll = () => {
      const navbar = document.querySelector(".navbar");
      if (!navbar || !titleContainerRef.current) return;

      const navbar_height = navbar.offsetHeight;

      // if (window.pageYOffset > 0) {
      //   navbar.classList.remove("single-project"); // Remove transparency for better readability when scrolling
      // } else {
      //   navbar.classList.add("single-project"); // Restore transparency on top
      // }

      // Calculate current absolute scroll vs element absolute layout position
      if (
        window.scrollY > titleTopRef.current - navbar_height &&
        window.scrollY > navbar_height
      ) {
        titleContainerRef.current.classList.add("is-header");
        titleContainerRef.current.style.height = `${navbar_height + parseInt(titleContainerHeight.current)}px`; // 4. Dynamically adjust title position based on actual navbar height
        navbar.classList.add("rmv-bg");
      } else {
        titleContainerRef.current.classList.remove("is-header");
        titleContainerRef.current.style.height = "auto"; // Reset height when not fixed
        navbar.classList.remove("rmv-bg");
      }
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", measureLayout); // Keep accurate on window resize

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", measureLayout); // Clean up any class changes on unmount
    };
  }, [id]); // Recalculate if project route id changes

  useEffect(() => {
    mediaRef.current.addEventListener("loadeddata", () => measureLayout());
    document.querySelector(".navbar").classList.add("single-project");
    return () => {
      document.querySelector(".navbar").classList.remove("single-project");
    };
  }, []);

  if (!projectData || !projectData.publish) {
    return (
      <div style={{ padding: "100px", textAlign: "center" }}>
        <h2>Proyecto no encontrado</h2>
      </div>
    );
  }
  return (
    <div>
      <div className="titleAndImageContainer">
        <div ref={titleContainerRef} className="titleContainer">
          <h2>{projectData.title}</h2>
          <p>{projectData.location}</p>
        </div>
        {imageList["banner"].split(".").slice(-1)[0] === "jpg" && (
          <img
            ref={mediaRef}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              objectFit: "cover",
              objectPosition: "center",
            }}
            src={imageList["banner"]}
            alt={projectData.title}
            onLoad={measureLayout} // 3. CRITICAL: Recalculate dimensions the exact millisecond the image renders
          />
        )}
        {imageList["banner"].split(".").slice(-1)[0] === "mp4" && (
          <video
            ref={mediaRef}
            style={{
              width: "100%",
              height: "auto",
              maxHeight: "80vh",
              objectFit: "cover",
              objectPosition: "center",
            }}
            src={imageList["banner"]}
            autoPlay
            loop
            muted
            onLoadedData={measureLayout} // Recalculate when video metadata is loaded
          />
        )}
      </div>
      <div className="projectDataContainer">
        <div className="description">
          <p>{projectData.description}</p>
        </div>
        <div className="stats">
          <p>
            <b>Uso:</b> {projectData.usage}
          </p>
          <p>
            <b>Estado:</b> {projectData.status}
          </p>
          <p>
            <b>Año:</b> {projectData.year}
          </p>
          <p>
            <b>Sup. Construida:</b> {projectData.constructionArea}
          </p>
          <p>
            <b>Fotografía:</b> {projectData.photography}
          </p>
        </div>
      </div>
      <div className="projectImageGallery">
        {imageList["gallery"].map((src, index) => {
          if (src.split(".").slice(-1)[0] === "jpg") {
            return (
              <img
                key={index}
                src={src}
                alt={`${projectData.title} - ${index + 2}`}
              />
            );
          } else if (src.split(".").slice(-1)[0] === "mp4") {
            return <video key={index} src={src} autoPlay loop muted />;
          }
          return null; // Skip unsupported formats
        })}
      </div>
    </div>
  );
}
